// Sprint E · Task 7 — print-to-PDF report view.
//
// The previous "Export PDF" button called window.print() on the live UI,
// which produced an unusable screenshot of the dashboard chrome. This
// component renders a clean, paginated A4 layout that:
//   - is portal-rendered to document.body (sibling of #root)
//   - hides itself on screen via display:none
//   - on print, #root is display:none so only this report prints
//   - paginates into 3 sections via CSS page-break-before
//
// Why portal + display:none rather than visibility:hidden on body *:
// the visibility approach kept the live dashboard's layout space,
// producing many blank pages before the report. display:none on #root
// removes the layout entirely.
//
// Deterministic: same site data → same DOM → same print render.
import React from 'react';
import { createPortal } from 'react-dom';
import { nullDisplay } from '../utils/fieldProvenance';

// Map the `c` color name returned by CRITERIA `c:` callbacks to a print
// bucket class (light tint backgrounds so the table is legible in B&W).
function colorToPrintBucket(color) {
  if (color === 'green') return 'good';
  if (color === 'amber') return 'warn';
  if (color === 'red')   return 'bad';
  return 'unknown';
}

export function PrintReport({ sites, computeScore, criteria }) {
  if (!sites || sites.length === 0) return null;
  const generatedAt = new Date().toISOString().slice(0, 10);

  return createPortal(
    <div className="print-report">
      {/* ─── PAGE 1 — Cover + per-site scorecard ─── */}
      <section className="pr-page">
        <header className="pr-cover-head">
          <div className="pr-brand">SEIDR · Site Comparison Report</div>
          <div className="pr-meta">Generated {generatedAt}</div>
        </header>

        <h1 className="pr-cover-title">
          {sites.length === 1
            ? sites[0]._project?.site_label || sites[0].nearest_grid_name || 'Site brief'
            : `${sites.length} sites compared`}
        </h1>

        <div className="pr-cover-grid">
          {sites.map(s => {
            const breakdown = computeScore(s);
            const p = s._project || {};
            return (
              <div key={p.id} className="pr-cover-card">
                <div className="pr-cover-name">{p.site_label || s.nearest_grid_name || '—'}</div>
                <div className="pr-cover-coords">
                  {p.lat != null && p.lon != null
                    ? `${Number(p.lat).toFixed(3)}, ${Number(p.lon).toFixed(3)}`
                    : ''}
                </div>
                <div className="pr-cover-score">
                  {breakdown.pct}<span className="pr-cover-score-suffix">/100</span>
                </div>
                <div className="pr-cover-rating">{breakdown.rating}</div>

                <div className="pr-cover-components">
                  {breakdown.components.map(c => (
                    <div key={c.name} className="pr-cover-comp">
                      <span className="pr-cover-comp-name">{c.name}</span>
                      <span className="pr-cover-comp-bar">
                        <span className="pr-cover-comp-bar-fill"
                              style={{ width: `${c.raw || 0}%` }} />
                      </span>
                      <span className="pr-cover-comp-val">{c.raw != null ? c.raw : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <footer className="pr-foot">
          <span>SEIDR site-finder · seidr.no</span>
          <span>Page 1 of 3</span>
        </footer>
      </section>

      {/* ─── PAGE 2 — Full criteria table mirroring the Compare view ─── */}
      <section className="pr-page pr-pagebreak">
        <header className="pr-page-head">
          <div className="pr-brand">SEIDR · Detailed criteria</div>
          <div className="pr-meta">{generatedAt}</div>
        </header>

        <table className="pr-detail-table">
          <thead>
            <tr>
              <th className="pr-detail-group">Group</th>
              <th className="pr-detail-factor">Criterion</th>
              {sites.map(s => (
                <th key={s._project.id} className="pr-detail-site">
                  {s._project?.site_label || s.nearest_grid_name || '—'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(criteria || []).map((cr, i) => {
              const prev = i > 0 ? criteria[i - 1] : null;
              const groupChanged = !prev || prev.g !== cr.g;
              return (
                <tr key={`${cr.g}-${cr.l}`} className={groupChanged ? 'pr-row-group-start' : ''}>
                  <td className="pr-detail-group">{groupChanged ? cr.g : ''}</td>
                  <td className="pr-detail-factor">{cr.l}</td>
                  {sites.map(s => {
                    const raw = cr.v(s);
                    // Mirror screen: explicit null-state when value is null
                    // and the criterion has field-level provenance.
                    const display = (raw == null && cr.field)
                      ? nullDisplay(s, cr.field)
                      : (cr.f ? cr.f(raw, s) : (raw != null ? String(raw) : '—'));
                    // Mirror severity tier from screen render.
                    const computed = cr.c(raw);
                    const color = cr.s === 'info' ? 'gray'
                                : (cr.s === 'opportunity' && computed === 'red') ? 'gray'
                                : computed;
                    const bucket = colorToPrintBucket(color);
                    return (
                      <td key={s._project.id}
                          className={`pr-detail-cell pr-bucket-${bucket}`}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Total row */}
            <tr className="pr-detail-total">
              <td colSpan={2}>SEIDR Index</td>
              {sites.map(s => {
                const b = computeScore(s);
                return (
                  <td key={s._project.id} className="pr-detail-cell">
                    <strong>{b.pct}</strong> — {b.rating}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        <footer className="pr-foot">
          <span>● Good  ● Warn  ● Bad  — bucket per documented thresholds (distanceThresholds.js)</span>
          <span>Page 2 of 3</span>
        </footer>
      </section>

      {/* ─── PAGE 3 — Provenance / data sources / what was not checked ─── */}
      <section className="pr-page pr-pagebreak">
        <header className="pr-page-head">
          <div className="pr-brand">SEIDR · Data sources & provenance</div>
          <div className="pr-meta">{generatedAt}</div>
        </header>

        <h2 className="pr-prov-title">Sources used</h2>
        <ul className="pr-prov-list">
          <li><strong>Power & grid:</strong> Fingrid (FI), NVE Nettanlegg / Geonorge (NO)</li>
          <li><strong>Industrial proximity (regulated):</strong> EEA Industrial Reporting v16 (IED + E-PRTR + LCP)</li>
          <li><strong>Industrial proximity (granular):</strong> OpenStreetMap Overpass</li>
          <li><strong>Cell coverage:</strong> OpenCelliD; OSM Overpass fallback</li>
          <li><strong>Subsea cables:</strong> TeleGeography</li>
          <li><strong>Broadband (FI):</strong> Traficom MoniNet — Helsinki region · 6 cities scope</li>
          <li><strong>Cultural heritage:</strong> Museovirasto (FI), Riksantikvaren (NO)</li>
          <li><strong>Protected areas:</strong> NVE / Naturbase (NO), OSM Overpass (FI)</li>
          <li><strong>Groundwater:</strong> SYKE Pohjavesialueet (FI)</li>
          <li><strong>Acid-sulfate soils:</strong> GTK (FI)</li>
          <li><strong>Species / red list:</strong> Laji.fi (FI)</li>
          <li><strong>Hazards:</strong> NVE Naturfare (NO/FI flood, landslide, quick-clay)</li>
          <li><strong>Climate (free-cooling):</strong> MET (NO), FMI (FI)</li>
          <li><strong>Terrain / slope:</strong> EU-DEM 25 m</li>
          <li><strong>Cadastre / area:</strong> Matrikkelen (NO), NLS (FI)</li>
        </ul>

        <h2 className="pr-prov-title">What was not scored</h2>
        <p className="pr-prov-text">
          Per site, factors with no available data were excluded from the
          weighted total (i.e. the denominator shrinks rather than the
          factor counting as zero). Re-run enrichment after fixing
          upstream API outages or providing manual data overrides.
        </p>

        <h2 className="pr-prov-title">Methodology — score</h2>
        <p className="pr-prov-text">
          Each factor produces a 0..1 sub-score against documented
          thresholds (see <code>distanceThresholds.js</code> +{' '}
          <code>calcScore</code>). Sub-scores are multiplied by per-factor
          weight, summed, and divided by the sum of weights for which
          data was available. Result is rounded to integer 0..100.
          Rating bands: ≥75 Suitable · ≥50 Conditional · ≥25 High-Risk · &lt;25 Not Suitable.
        </p>

        <h2 className="pr-prov-title">Methodology — industrial proximity</h2>
        <p className="pr-prov-text">
          For each of 8 facility categories, the nearest distance from
          EEA (where applicable) and OSM (where applicable) is shown.
          Categories with both sources collapse when distances agree
          within 2 km; otherwise both are surfaced with explicit source
          tags. Category warn thresholds: refinery 5 km · chemical 2 km ·
          power generation 1 km · industrial / agriculture 0.5 km ·
          fuel storage 1 km · gas pipeline 0.2 km.
        </p>

        <footer className="pr-foot">
          <span>Generated {generatedAt} · Deterministic (same data → same render)</span>
          <span>Page 3 of 3</span>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
